/**
 * 🆕 [Phase 51] OrganisationBTDialog — vue centralisée des employés cédulés
 * sur ≥ 2 BT pour une date donnée, avec dropdowns pour définir l'ordre.
 *
 * Endpoints utilisés :
 *   GET  /api/organisation-bt/jour/{date}
 *   PUT  /api/organisation-bt/jour/{date}/employe/{emp_id}
 *
 * Spec :
 * - Mobile-friendly (dropdowns 1er/2e/3e plutôt que drag&drop)
 * - Sauvegarde au changement (PUT debounced ou direct)
 * - Affiche un badge "✅ Défini" / "⚠ À définir" par employé
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader, AlertTriangle, CheckCircle2, Users, ListOrdered, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const ORDINAUX = ['1er', '2e', '3e', '4e', '5e', '6e'];

export function OrganisationBTDialog({ open, onClose, date, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [savingByEmp, setSavingByEmp] = useState({});  // {emp_id: true}
  // Sélections locales par employé : { emp_id: { bt_id: position_index } }
  const [localOrdres, setLocalOrdres] = useState({});
  // 🆕 [Phase 51 bonus] Prévisualisation du message d'horaire (debounced)
  const [previewMessage, setPreviewMessage] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/organisation-bt/jour/${date}`);
      setData(res.data);
      // Init local ordres depuis bt_ids_ordered si présent
      const init = {};
      (res.data?.employes || []).forEach(e => {
        const m = {};
        if (Array.isArray(e.bt_ids_ordered)) {
          e.bt_ids_ordered.forEach((bid, idx) => { m[bid] = idx; });
        }
        init[e.employee_id] = m;
      });
      setLocalOrdres(init);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur de chargement');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    if (open) reload();
  }, [open, reload]);

  // 🆕 [Phase 51 bonus] Recharger la prévisualisation du message à chaque changement
  // (employes/ordres/réload). Debouncée à 400ms pour éviter le spam.
  useEffect(() => {
    if (!open || !date) return;
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/production-calendar/message-horaire/preview`, {
          params: { date },
        });
        setPreviewMessage(res.data?.message || '');
      } catch (err) {
        setPreviewMessage('');
      } finally {
        setPreviewLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [open, date, data]);

  const handleChangeOrdre = (empId, btId, positionIdx) => {
    setLocalOrdres(prev => ({
      ...prev,
      [empId]: { ...(prev[empId] || {}), [btId]: positionIdx },
    }));
  };

  const handleSaveEmp = async (emp) => {
    const map = localOrdres[emp.employee_id] || {};
    const bts = emp.bts;
    // Vérifier : toutes les positions remplies + pas de doublon
    const positions = bts.map(b => map[b.id]);
    const allSet = positions.every(p => Number.isInteger(p));
    if (!allSet) {
      toast.error(`Définis une position pour chaque BT de ${emp.employee_nom}`);
      return;
    }
    const uniques = new Set(positions);
    if (uniques.size !== positions.length) {
      toast.error(`Chaque BT doit avoir une position différente (${emp.employee_nom})`);
      return;
    }
    // Construire la liste ordonnée
    const ordered = [...bts].sort((a, b) => map[a.id] - map[b.id]).map(b => b.id);
    setSavingByEmp(prev => ({ ...prev, [emp.employee_id]: true }));
    try {
      await axios.put(`${API_URL}/api/organisation-bt/jour/${date}/employe/${emp.employee_id}`, {
        bt_ids_ordered: ordered,
      });
      toast.success(`✅ Ordre enregistré pour ${emp.employee_nom}`);
      await reload();
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSavingByEmp(prev => ({ ...prev, [emp.employee_id]: false }));
    }
  };

  const employes = data?.employes || [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent
        className="max-w-2xl w-[calc(100%-1.5rem)] max-h-[90vh] overflow-y-auto"
        data-testid="organisation-bt-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListOrdered className="w-5 h-5 text-blue-600 shrink-0" />
            Organisation des bons de travail
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Date : <strong>{date}</strong> — définis l&apos;ordre des chantiers
            pour chaque employé cédulé sur plusieurs BT.
          </p>
        </DialogHeader>

        <div className="py-3 space-y-3">
          {loading && (
            <div className="flex justify-center py-8" data-testid="org-bt-loading">
              <Loader className="w-5 h-5 animate-spin text-slate-500" />
            </div>
          )}

          {!loading && employes.length === 0 && (
            <div className="text-center py-8 text-slate-500 italic" data-testid="org-bt-empty">
              <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
              Aucun employé n&apos;est cédulé sur plus d&apos;un bon de travail ce jour-là.
              <br />
              <span className="text-xs">Rien à organiser ✨</span>
            </div>
          )}

          {!loading && employes.map(emp => {
            const map = localOrdres[emp.employee_id] || {};
            const nbBts = emp.bts.length;
            const isOk = emp.ok;
            const isSaving = !!savingByEmp[emp.employee_id];
            return (
              <div
                key={emp.employee_id}
                className={`rounded-lg border p-3 ${isOk ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}
                data-testid={`org-bt-emp-${emp.employee_id}`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Users className="w-4 h-4 text-slate-600 shrink-0" />
                    <strong className="text-sm truncate" data-testid={`org-bt-emp-nom-${emp.employee_id}`}>
                      {emp.employee_nom}
                    </strong>
                    {isOk ? (
                      <span className="text-[10px] bg-emerald-200 text-emerald-800 px-1.5 py-0.5 rounded-full font-semibold shrink-0">
                        ✅ Défini
                      </span>
                    ) : (
                      <span className="text-[10px] bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full font-semibold shrink-0">
                        ⚠ À définir
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-slate-500 shrink-0">{nbBts} BT</span>
                </div>

                <ul className="space-y-1.5">
                  {emp.bts.map(bt => (
                    <li
                      key={bt.id}
                      className="flex items-center gap-2 bg-white rounded border border-slate-200 p-2 text-sm"
                      data-testid={`org-bt-row-${emp.employee_id}-${bt.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-800 truncate">
                          {bt.client_nom || bt.nom_dossier || '(sans nom)'}
                        </div>
                        {bt.numero_bt && (
                          <div className="text-[11px] text-slate-500 truncate">
                            {bt.numero_bt}
                          </div>
                        )}
                      </div>
                      <Select
                        value={map[bt.id] !== undefined ? String(map[bt.id]) : ''}
                        onValueChange={(v) => handleChangeOrdre(emp.employee_id, bt.id, Number(v))}
                      >
                        <SelectTrigger className="w-20 shrink-0" data-testid={`org-bt-select-${emp.employee_id}-${bt.id}`}>
                          <SelectValue placeholder="Pos." />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: nbBts }).map((_, idx) => (
                            <SelectItem key={idx} value={String(idx)}>
                              {ORDINAUX[idx] || `${idx + 1}e`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </li>
                  ))}
                </ul>

                <div className="flex justify-end mt-2">
                  <Button
                    size="sm"
                    onClick={() => handleSaveEmp(emp)}
                    disabled={isSaving}
                    className="h-8"
                    data-testid={`org-bt-save-${emp.employee_id}`}
                  >
                    {isSaving ? <Loader className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                    Enregistrer
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="org-bt-close">
            Fermer
          </Button>
        </DialogFooter>

        {/* 🆕 [Phase 51 bonus] Prévisualisation du message d'horaire */}
        <div className="border-t mt-2 pt-3" data-testid="org-bt-preview-section">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 mb-2">
            <MessageCircle className="w-4 h-4 text-blue-500" />
            Aperçu du message d&apos;horaire
            {previewLoading && <Loader className="w-3 h-3 animate-spin ml-1 text-slate-400" />}
            {data && data.nb_employes_sans_ordre > 0 && (
              <span className="ml-auto text-amber-700 text-[10px] font-medium">
                ⚠ Définis l&apos;ordre pour tous les employés pour voir l&apos;aperçu complet
              </span>
            )}
          </div>
          <pre
            className="text-[11px] leading-relaxed font-mono bg-slate-50 border rounded p-3 max-h-48 overflow-auto whitespace-pre-wrap"
            data-testid="org-bt-preview-text"
          >
            {previewMessage || (previewLoading ? '...' : '(aucun message à afficher)')}
          </pre>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Badge proactif à insérer dans le header du calendrier de production.
 * Affiche "🔴 N employés à organiser" UNIQUEMENT quand des employés ont un
 * ordre manquant. Sinon invisible (null).
 *
 * Props : date (YYYY-MM-DD), onClick (ouvre OrganisationBTDialog)
 *         refreshKey (incrémenter pour forcer un re-fetch)
 */
export function OrganisationBTBadge({ date, onClick, refreshKey = 0 }) {
  const [nbSansOrdre, setNbSansOrdre] = useState(0);
  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    axios.get(`${API_URL}/api/organisation-bt/check-ordre-defini`, { params: { date } })
      .then(res => {
        if (cancelled) return;
        const sansOrdre = res.data?.employes_sans_ordre || [];
        setNbSansOrdre(sansOrdre.length);
      })
      .catch(() => !cancelled && setNbSansOrdre(0));
    return () => { cancelled = true; };
  }, [date, refreshKey]);

  if (nbSansOrdre === 0) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 bg-red-100 text-red-800 hover:bg-red-200 border border-red-300 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors animate-pulse"
      title="Cliquer pour organiser les BT en double"
      data-testid="org-bt-badge"
    >
      <AlertTriangle className="w-3.5 h-3.5" />
      {nbSansOrdre} employé{nbSansOrdre > 1 ? 's' : ''} à organiser
    </button>
  );
}

export default OrganisationBTDialog;
